import React, { useEffect, useState } from "react";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Full-screen modal that hosts the official PayPal Smart Payment Buttons.
 * Two-step flow — createOrder on the backend, capture on the backend once
 * the user approves in the PayPal popup. We never touch the payment amount
 * on the client so it can't be tampered with.
 */
export default function PayPalPayModal({ kind, orderId, amount, onSuccess, onClose }) {
  const [clientId, setClientId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/paypal/config`).then((r) => {
      if (r.data?.configured && r.data?.client_id) setClientId(r.data.client_id);
      else setErr("PayPal non configurato lato server");
    }).catch(() => setErr("Impossibile caricare PayPal"));
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="paypal-pay-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#4a1c17] text-[#f7ebdf] px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg font-bold">Paga con PayPal</div>
            <div className="text-xs opacity-80">Totale: € {Number(amount || 0).toFixed(2)}</div>
          </div>
          <button onClick={onClose} data-testid="paypal-close" className="p-1.5 rounded-full hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 min-h-[220px] flex items-center justify-center">
          {err ? (
            <div className="text-sm text-red-700 text-center">{err}</div>
          ) : !clientId ? (
            <Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" />
          ) : (
            <div className="w-full">
              <PayPalScriptProvider options={{ "client-id": clientId, currency: "EUR", intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", shape: "rect", color: "gold", label: "paypal" }}
                  createOrder={async () => {
                    try {
                      const { data } = await axios.post(`${API}/paypal/orders/${kind}/${orderId}`);
                      return data.paypal_order_id;
                    } catch (e) {
                      setErr(e?.response?.data?.detail || "Errore creazione ordine PayPal");
                      throw e;
                    }
                  }}
                  onApprove={async () => {
                    try {
                      await axios.post(`${API}/paypal/orders/${kind}/${orderId}/capture`);
                      onSuccess?.();
                    } catch (e) {
                      setErr(e?.response?.data?.detail || "Errore conferma pagamento");
                    }
                  }}
                  onCancel={() => setErr("")}
                  onError={(e) => setErr(String(e?.message || e))}
                />
              </PayPalScriptProvider>
              <p className="text-[11px] text-[#7c5a4e] mt-3 text-center">
                Tramite PayPal puoi pagare anche con carta di credito senza registrarti.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
